import React, { useState, useEffect, useCallback } from 'react';
import * as ExcelJS from 'exceljs';
import Metrics from './Metrics';
import TeamMembersTableSkeleton from './TeamMembersTableSkeleton';
import type { MetricsData, SortConfig, TeamMember } from './types';
import './TeamMembersOverview.css';

interface TeamMembersOverviewProps {
  teamMembers: TeamMember[];
  metrics: Pick<MetricsData, 'dueDate' | 'startDate'>;
  isLoading: boolean;
  onMemberClick: (member: TeamMember) => void;
  onExportReady?: (fn: () => Promise<void>) => void;
}

const EMPTY_METRICS: MetricsData = {
  committedSPs: 0,
  completedSPs: 0,
  progress: 0,
  assignedTasks: 0,
  completedAssignedTasks: 0,
  workingCapacity: 0,
  loggedHours: 0,
  estimated: 0,
  remaining: 0,
  reworkbytask: 0,
  daysleft: 0,
  timeEfficiency: 0,
  timePerSP: 'N/A',
  timePerTask: 'N/A',
  numEmployees: 0,
  dueDate: '',
  startDate: '',
  actualCapacity: 0,
  totalSprintCapacity: 0,
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const calculateTotalMetrics = (
  teamMembers: TeamMember[],
  dueDate: string,
  startDate: string,
): MetricsData =>
{
  if (!teamMembers.length) return EMPTY_METRICS;

  const totals = teamMembers.reduce(
    (acc, m) => ({
      committedSPs: acc.committedSPs + (m.CommittedSPs || 0),
      completedSPs: acc.completedSPs + (m.CompletedSPs || 0),
      assignedTasks: acc.assignedTasks + (m.AssignedTasks || 0),
      completedAssignedTasks: acc.completedAssignedTasks + (m.CompletedAssignedTasks || 0),
      workingCapacity: acc.workingCapacity + (m.WorkingCapacity || 0),
      actualCapacity: acc.actualCapacity + (m.ActualCapacity || 0),
      totalSprintCapacity: acc.totalSprintCapacity + (m.TotalSprintCapacity || 0),
      loggedHours: round2(acc.loggedHours + (m.LoggedHours || 0)),
      estimated: round2(acc.estimated + (m.Estimated || 0)),
      remaining: round2(acc.remaining + (m.Remaining || 0)),
      reworkSum: round2(acc.reworkSum + (m.ReworkByTask || 0)),
      progressSum: round2(acc.progressSum + (m.Progress || 0)),
      timeEfficiencySum: round2(acc.timeEfficiencySum + (m.TimeEfficiency || 0)),
    }),
    {
      committedSPs: 0,
      completedSPs: 0,
      assignedTasks: 0,
      completedAssignedTasks: 0,
      workingCapacity: 0,
      actualCapacity: 0,
      totalSprintCapacity: 0,
      loggedHours: 0,
      estimated: 0,
      remaining: 0,
      reworkSum: 0,
      progressSum: 0,
      timeEfficiencySum: 0,
    },
  );

  const count = teamMembers.length;
  const progress = round2(totals.progressSum / count);
  const reworkbytask = round2(totals.reworkSum / count);
  const timeEfficiency = round2(totals.timeEfficiencySum / count);
  const timePerSP: number | 'N/A' =
    totals.completedSPs > 0 ? round2(totals.loggedHours / totals.completedSPs) : 'N/A';
  const timePerTask: number | 'N/A' =
    totals.completedAssignedTasks > 0
      ? round2(totals.loggedHours / totals.completedAssignedTasks)
      : 'N/A';
  const daysleft = teamMembers[0]?.DaysLeft ?? 0;

  return {
    ...totals,
    progress,
    reworkbytask,
    timeEfficiency,
    timePerSP,
    timePerTask,
    daysleft,
    numEmployees: count,
    dueDate,
    startDate,
  };
};

const sortTeamMembers = (members: TeamMember[], sortConfig: SortConfig): TeamMember[] =>
{
  if (!sortConfig.key || !sortConfig.direction) return members;

  return [...members].sort((a, b) =>
  {
    let aVal: number | string = 0;
    let bVal: number | string = 0;

    switch (sortConfig.key)
    {
      case 'name':
        aVal = a.Name || '';
        bVal = b.Name || '';
        break;
      case 'progress':
        aVal = a.Progress || 0;
        bVal = b.Progress || 0;
        break;
      case 'storyPoints':
        aVal = a.CommittedSPs || 0;
        bVal = b.CommittedSPs || 0;
        break;
      case 'completedTasks':
        aVal = a.AssignedTasks > 0 ? a.CompletedAssignedTasks / a.AssignedTasks : 0;
        bVal = b.AssignedTasks > 0 ? b.CompletedAssignedTasks / b.AssignedTasks : 0;
        break;
      case 'rework':
        aVal = a.ReworkByTask || 0;
        bVal = b.ReworkByTask || 0;
        break;
      case 'workingCapacity':
        aVal = a.WorkingCapacity || 0;
        bVal = b.WorkingCapacity || 0;
        break;
      case 'actualCapacity':
        aVal = a.ActualCapacity || 0;
        bVal = b.ActualCapacity || 0;
        break;
      case 'loggedHours':
        aVal = a.LoggedHours || 0;
        bVal = b.LoggedHours || 0;
        break;
      case 'estimated':
        aVal = a.Estimated || 0;
        bVal = b.Estimated || 0;
        break;
      case 'remaining':
        aVal = a.Remaining || 0;
        bVal = b.Remaining || 0;
        break;
      case 'timePerSP':
        aVal = typeof a.TimePerSP === 'number' ? a.TimePerSP : 0;
        bVal = typeof b.TimePerSP === 'number' ? b.TimePerSP : 0;
        break;
      case 'timePerTask':
        aVal = typeof a.TimePerTask === 'number' ? a.TimePerTask : 0;
        bVal = typeof b.TimePerTask === 'number' ? b.TimePerTask : 0;
        break;
      case 'efficiency':
        aVal = a.TimeEfficiency || 0;
        bVal = b.TimeEfficiency || 0;
        break;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string')
    {
      const cmp = aVal.localeCompare(bVal);
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
};

const getEfficiencyClass = (eff: number) =>
{
  if (eff >= 70) return 'efficiency-high';
  if (eff >= 60) return 'efficiency-medium';
  return 'efficiency-low';
};

const SortIcon: React.FC<{ columnKey: string; sortConfig: SortConfig }> = ({
  columnKey,
  sortConfig,
}) =>
{
  if (sortConfig.key !== columnKey || !sortConfig.direction)
  {
    return (
      <span
        className="fa fa-sort"
        style={{ display: 'inline-block', width: 12, textAlign: 'right' }}
      />
    );
  }
  return (
    <span
      className={sortConfig.direction === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'}
      style={{ display: 'inline-block', width: 12, textAlign: 'right' }}
    />
  );
};

const TeamMembersOverview: React.FC<TeamMembersOverviewProps> = ({
  teamMembers,
  metrics,
  isLoading,
  onMemberClick,
  onExportReady,
}) =>
{
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });
  const [totalMetrics, setTotalMetrics] = useState<MetricsData>(EMPTY_METRICS);

  useEffect(() =>
  {
    setTotalMetrics(calculateTotalMetrics(teamMembers, metrics.dueDate, metrics.startDate));
  }, [teamMembers, metrics.dueDate, metrics.startDate]);

  const handleSort = useCallback((key: string) =>
  {
    setSortConfig(prev =>
    {
      if (prev.key === key)
      {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const sortedMembers = sortTeamMembers(teamMembers, sortConfig);
  const hasStoryPoints = totalMetrics.committedSPs > 0;
  const gridClass = hasStoryPoints ? '' : 'no-story-points';

  // Keep a ref so the stable callback always uses the latest sorted members
  const sortedMembersRef = React.useRef(sortedMembers);
  sortedMembersRef.current = sortedMembers;

  const downloadExcel = useCallback(async () =>
  {
    const members = sortedMembersRef.current;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Team Members Report', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });

    const headers = [
      { name: 'EmployeeID', key: 'personId', width: 15 },
      { name: 'Team Members', key: 'teamMember', width: 30 },
      { name: 'Progress', key: 'progress', width: 12 },
      { name: 'Story Points', key: 'storyPoints', width: 15 },
      { name: 'Completed Tasks', key: 'completedTasks', width: 18 },
      { name: 'Rework', key: 'rework', width: 10 },
      { name: 'Actual Capacity', key: 'actualCapacity', width: 18 },
      { name: 'Logged Hours', key: 'loggedHours', width: 15 },
      { name: 'Estimated', key: 'estimated', width: 12 },
      { name: 'Remaining', key: 'remaining', width: 12 },
      { name: 'Time Per SP', key: 'timePerSP', width: 12 },
      { name: 'Time Per Task', key: 'timePerTask', width: 15 },
      { name: 'Efficiency', key: 'efficiency', width: 12 },
    ];

    worksheet.columns = headers;
    const headerRow = worksheet.addRow(headers.map(h => h.name));
    headerRow.eachCell(cell =>
    {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    members.forEach((member, index) =>
    {
      const row = worksheet.addRow({
        personId: member.PersonId,
        teamMember: member.Name,
        progress: `${member.Progress}%`,
        storyPoints: `${member.CompletedSPs}/${member.CommittedSPs}`,
        completedTasks: `${member.CompletedAssignedTasks}/${member.AssignedTasks}`,
        rework: `${member.ReworkByTask}%`,
        actualCapacity: member.ActualCapacity,
        loggedHours: member.LoggedHours,
        estimated: member.Estimated,
        remaining: member.Remaining,
        timePerSP: member.TimePerSP || 'N/A',
        timePerTask: member.TimePerTask,
        efficiency: `${member.TimeEfficiency}%`,
      });

      row.eachCell((cell, col) =>
      {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        if (col === 13)
        {
          const eff = member.TimeEfficiency;
          cell.font = {
            color: { argb: eff >= 70 ? 'FF008000' : eff < 60 ? 'FFFF0000' : 'FFFF6600' },
            bold: true,
          };
        }
        if (col === 6 && member.ReworkByTask > 0)
        {
          cell.font = { color: { argb: 'FFFF6600' } };
        }
      });

      if (index % 2 === 1)
      {
        row.eachCell(cell =>
        {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
        });
      }
    });

    const now = new Date();
    const dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${now.getFullYear()}`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Team_Members_Report_${dateStr}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  useEffect(() =>
  {
    onExportReady?.(downloadExcel);
  }, [downloadExcel, onExportReady]);

  if (isLoading)
  {
    return (
      <div className="meview-team-overview">
        <div className="main-content-container">
          <div className="metrics-section">
            <Metrics data={totalMetrics} isEnableSkeleton />
          </div>
          <div className="table-section">
            <TeamMembersTableSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!sortedMembers.length)
  {
    return (
      <div className="meview-team-overview">
        <div className="empty-state">
          <p>No team members data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="meview-team-overview">
      <div className="main-content-container">
        <div className="metrics-section">
          <Metrics data={totalMetrics} isEnableSkeleton={false} />
        </div>

        <div className="table-section">
          <div className={`team-members-table ${gridClass}`}>
            {/* Header */}
            <div className="table-header-tm">
              <div className="header-cell user-cell" onClick={() => handleSort('name')}>
                <span>Team Members</span>
                <span className="members-count">({sortedMembers.length})</span>
                <SortIcon columnKey="name" sortConfig={sortConfig} />
              </div>
              <div className="header-cell progress-cell" onClick={() => handleSort('progress')}>
                Progress <SortIcon columnKey="progress" sortConfig={sortConfig} />
              </div>
              {hasStoryPoints && (
                <div
                  className="header-cell story-points-cell"
                  onClick={() => handleSort('storyPoints')}
                >
                  Story Points <SortIcon columnKey="storyPoints" sortConfig={sortConfig} />
                </div>
              )}
              <div
                className="header-cell completed-tasks-cell"
                onClick={() => handleSort('completedTasks')}
              >
                Completed Tasks <SortIcon columnKey="completedTasks" sortConfig={sortConfig} />
              </div>
              <div className="header-cell rework-cell" onClick={() => handleSort('rework')}>
                Rework <SortIcon columnKey="rework" sortConfig={sortConfig} />
              </div>
              <div
                className="header-cell working-capacity-cell"
                onClick={() => handleSort('actualCapacity')}
              >
                Actual Capacity <SortIcon columnKey="actualCapacity" sortConfig={sortConfig} />
              </div>
              <div
                className="header-cell logged-hours-cell"
                onClick={() => handleSort('loggedHours')}
              >
                Logged Hours <SortIcon columnKey="loggedHours" sortConfig={sortConfig} />
              </div>
              <div className="header-cell estimated-cell" onClick={() => handleSort('estimated')}>
                Estimated <SortIcon columnKey="estimated" sortConfig={sortConfig} />
              </div>
              <div className="header-cell remaining-cell" onClick={() => handleSort('remaining')}>
                Remaining <SortIcon columnKey="remaining" sortConfig={sortConfig} />
              </div>
              {hasStoryPoints ? (
                <div className="header-cell timepersp-cell" onClick={() => handleSort('timePerSP')}>
                  Time Per SP <SortIcon columnKey="timePerSP" sortConfig={sortConfig} />
                </div>
              ) : (
                <div
                  className="header-cell timepertask-cell"
                  onClick={() => handleSort('timePerTask')}
                >
                  Time Per Task <SortIcon columnKey="timePerTask" sortConfig={sortConfig} />
                </div>
              )}
              <div className="header-cell efficiency-cell" onClick={() => handleSort('efficiency')}>
                Efficiency <SortIcon columnKey="efficiency" sortConfig={sortConfig} />
              </div>
            </div>

            {/* Body */}
            <div className="table-body-vm">
              {sortedMembers.map((member, index) => (
                <div
                  key={member.PersonId ?? index}
                  className="table-row-vm"
                  onClick={() => onMemberClick(member)}
                >
                  <div className="table-cell user-cell">
                    <div className="user-info">
                      <div
                        className="user-avatar"
                        style={{ backgroundImage: `url(${member.ImageName})` }}
                      />
                      <div className="user-name">{member.Name}</div>
                    </div>
                  </div>

                  <div className="table-cell progress-cell">
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${member.Progress}%` }} />
                      </div>
                      <span className="progress-text">{member.Progress}%</span>
                    </div>
                  </div>

                  {hasStoryPoints && (
                    <div className="table-cell story-points-cell">
                      {member.CompletedSPs}/{member.CommittedSPs}
                    </div>
                  )}

                  <div className="table-cell completed-tasks-cell">
                    {member.CompletedAssignedTasks}/{member.AssignedTasks}
                  </div>

                  <div className="table-cell rework-cell">
                    <span className="rework-pct">{member.ReworkByTask}%</span>
                  </div>

                  <div className="table-cell working-capacity-cell">{member.ActualCapacity} h</div>

                  <div className="table-cell logged-hours-cell">{member.LoggedHours} h</div>

                  <div className="table-cell estimated-cell">{member.Estimated} h</div>

                  <div className="table-cell remaining-cell">{member.Remaining} h</div>

                  {hasStoryPoints ? (
                    <div className="table-cell timepersp-cell">
                      {member.TimePerSP === 'N/A' ? '0' : member.TimePerSP} h/sp
                    </div>
                  ) : (
                    <div className="table-cell timepertask-cell">
                      {member.TimePerTask === 'N/A' ? '0' : member.TimePerTask} h/task
                    </div>
                  )}

                  <div className="table-cell efficiency-cell">
                    <span className={getEfficiencyClass(member.TimeEfficiency)}>
                      {member.TimeEfficiency}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMembersOverview;
